import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch } from '@/store'
import * as Types from './types'
import { fetchGuideInfoApi } from '@/services/api/guide'
import { postUpdateSetting, getUserSettingList } from '@/services/api/user'
import { SETTING_CODE } from '@/constants'
import type { SettingKeyType, SettingUpdateParams } from '@/services/api/user'
import { getTargetOmsConfig } from '@/utils/getConfig'

const initialState: Types.CommonState = {
  guideInfo: null,
  messageConfig: null,
  downloadConfig: null,
  userSettingIsReady: false,
  setting: {
    CURRENCY_RATE: {
      dataValue: '0',
      dataType: null,
      settingCode: 'CURRENCY_RATE',
    },
    [SETTING_CODE.SALES_SORT_FEATURE]: {
      settingCode: SETTING_CODE.SALES_SORT_FEATURE,
      dataType: null,
      dataValue: '',
    },
  },
}

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setGuideInfo(state, action) {
      const data = action.payload
      // 过滤废弃的任务
      const taskDetailResBos = data?.taskDetailResBos?.filter((i) => !i.disable)
      const process = taskDetailResBos?.filter((i) => i.completeStatus).length
      return {
        ...state,
        guideInfo: {
          ...action.payload,
          process,
          taskDetailResBos,
        },
      }
    },
    setSetting(state, action: PayloadAction<Types.CommonState['setting']>): Types.CommonState {
      return {
        ...state,
        setting: {
          ...state.setting,
          ...action.payload,
        },
      }
    },
    updateMessageConfig(state, action: PayloadAction<Types.IMessageConfigRes>) {
      return {
        ...state,
        messageConfig: action.payload.message,
        downloadConfig: action.payload.download,
      }
    },
    updateUserSettingIsReady(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        userSettingIsReady: action.payload,
      }
    },
  },
})

/** 获取用户配置 */
export const fetchUserSetting = (codes?: SettingKeyType[]) => async (dispatch: AppDispatch) => {
  const response = await getUserSettingList({ setting_codes: codes })
  const { settings } = response?.data || {}
  if (Array.isArray(settings)) {
    const settingMap = settings.reduce((sum, cur) => {
      const key = cur.settingCode
      sum[key] = cur
      return sum
    }, {})

    dispatch(setSetting(settingMap))
    dispatch(updateUserSettingIsReady(true))
    return settings
  }
}

/** 新手指引信息 */
export const fetchGuideInfo = () => async (dispatch: AppDispatch) => {
  const response = await fetchGuideInfoApi()
  const { data } = response
  dispatch(setGuideInfo(data))
  return data
}

/** 更新用户配置 */
export const updateSetting = (params: SettingUpdateParams[]) => async (dispatch: AppDispatch) => {
  const response = await postUpdateSetting(params)
  const { data } = response

  // 更新对应的store
  const settingMap = params.reduce((sum, cur) => {
    const key = cur.settingCode
    sum[key] = cur
    return sum
  }, {})

  dispatch(setSetting(settingMap))
  return data
}

export const getMessageCenterConfig = () => async (dispatch: AppDispatch) => {
  const data = await getTargetOmsConfig('messageCenterConfig')
  dispatch(updateMessageConfig(data))
}

export const { setGuideInfo, setSetting, updateMessageConfig, updateUserSettingIsReady } = commonSlice.actions
export default commonSlice.reducer
